import { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  updateDoc, 
  doc, 
  orderBy, 
  limit,
  Timestamp,
  getDoc
} from 'firebase/firestore';
import { auth, firestore } from '../services/firebase';
import { Notification } from '../types';
import { useNotification } from '../context/NotificationContext';

export const useNotificationsListener = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [friendRequestsCount, setFriendRequestsCount] = useState(0);
  const [sharedRoutinesCount, setSharedRoutinesCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const { showToast } = useNotification();

  // Marcar todas las notificaciones como leídas
  const markAllAsRead = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;

      // Actualizar solo las no leídas
      const unreadNotifications = notifications.filter(notification => !notification.read);
      
      // Actualizar en la base de datos
      const updatePromises = unreadNotifications.map(notification => 
        updateDoc(doc(firestore, 'notifications', notification.id), {
          read: true
        })
      );
      
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error al marcar notificaciones como leídas:', error);
    }
  };

  // Marcar todas las notificaciones de un tipo específico como leídas
  const markTypeAsRead = async (type: 'friend_request' | 'routine_shared' | 'friend_accepted') => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;

      // Actualizar solo las no leídas del tipo especificado
      const typedUnreadNotifications = notifications.filter(
        notification => !notification.read && notification.type === type
      );
      
      // Actualizar en la base de datos
      const updatePromises = typedUnreadNotifications.map(notification => 
        updateDoc(doc(firestore, 'notifications', notification.id), {
          read: true
        })
      );
      
      await Promise.all(updatePromises);
    } catch (error) {
      console.error(`Error al marcar notificaciones de tipo ${type} como leídas:`, error);
    }
  };

  // Marcar todas las rutinas compartidas como leídas (usado cuando se entra a la pantalla de rutinas)
  const markRoutineNotificationsAsRead = async () => {
    await markTypeAsRead('routine_shared');
  };

  // Marcar todas las solicitudes de amistad como leídas (usado cuando se entra a la pantalla de gestión de amigos)
  const markFriendRequestNotificationsAsRead = async () => {
    await markTypeAsRead('friend_request');
    await markTypeAsRead('friend_accepted');
  };

  // Marcar una notificación específica como leída
  const markAsRead = async (notificationId: string) => {
    try {
      await updateDoc(doc(firestore, 'notifications', notificationId), {
        read: true
      });
    } catch (error) {
      console.error('Error al marcar notificación como leída:', error);
    }
  };

  // Obtener datos adicionales para mostrar notificaciones más informativas
  const getRelatedData = async (notification: Notification) => {
    try {
      if (notification.relatedId) {
        if (notification.type === 'friend_request' || notification.type === 'friend_accepted') {
          // Obtener datos del usuario
          const userDoc = await getDoc(doc(firestore, 'users', notification.relatedId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            return {
              ...notification,
              relatedData: {
                name: `${userData.firstName} ${userData.lastName}`,
                username: userData.username
              }
            };
          }
        } else if (notification.type === 'routine_shared') {
          // Obtener datos de la rutina
          const routineDoc = await getDoc(doc(firestore, 'routines', notification.relatedId));
          if (routineDoc.exists()) {
            const routineData = routineDoc.data();

            // También obtener datos del creador
            const creatorDoc = await getDoc(doc(firestore, 'users', routineData.creatorId));
            let creatorName = 'Alguien';
            
            if (creatorDoc.exists()) {
              const creatorData = creatorDoc.data();
              creatorName = `${creatorData.firstName} ${creatorData.lastName}`;
            }
            
            return {
              ...notification,
              relatedData: {
                routineName: routineData.name,
                creatorName
              }
            };
          }
        }
      }
      return notification;
    } catch (error) {
      console.error('Error al obtener datos relacionados:', error);
      return notification;
    }
  };

  useEffect(() => {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      setLoading(false);
      return;
    }

    console.log("Inicializando listener de notificaciones para", userId);

    // Consulta para obtener las notificaciones del usuario actual
    const notificationsQuery = query(
      collection(firestore, 'notifications'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(50) // Limitar a las 50 más recientes
    );

    // Suscripción a cambios en notificaciones
    const unsubscribe = onSnapshot(notificationsQuery, async (snapshot) => {
      console.log(`Recibidos ${snapshot.docs.length} documentos de notificaciones`);
      
      const notificationsData: Notification[] = [];
      
      for (const docSnapshot of snapshot.docs) {
        const data = docSnapshot.data();
        
        const notificationData: Notification = {
          id: docSnapshot.id,
          userId: data.userId,
          type: data.type,
          title: data.title,
          message: data.message,
          relatedId: data.relatedId,
          read: data.read,
          displayed: data.displayed,
          createdAt: 0 // Valor por defecto, se actualizará a continuación
        };
        
        // Convertir Timestamp a número (milisegundos)
        if (data.createdAt) {
          if (data.createdAt instanceof Timestamp) {
            notificationData.createdAt = data.createdAt.toDate().getTime();
          } else if (typeof data.createdAt === 'number') {
            notificationData.createdAt = data.createdAt;
          }
        }
        
        notificationsData.push(notificationData);
      }
      
      // Obtener las no leídas y no mostradas
      const unreadNotifications = notificationsData.filter(n => !n.read);
      const notDisplayedNotifications = notificationsData.filter(n => !n.displayed);
      
      console.log(`Notificaciones sin leer: ${unreadNotifications.length}`);
      console.log(`Notificaciones sin mostrar: ${notDisplayedNotifications.length}`);
      
      // Filtrar por tipo para contadores específicos
      const friendRequestNotifications = unreadNotifications.filter(
        n => n.type === 'friend_request' || n.type === 'friend_accepted'
      );
      const routineNotifications = unreadNotifications.filter(
        n => n.type === 'routine_shared'
      );
      
      // Actualizar estados
      setNotifications(notificationsData);
      setUnreadCount(unreadNotifications.length);
      setFriendRequestsCount(friendRequestNotifications.length);
      setSharedRoutinesCount(routineNotifications.length);
      setLoading(false);
      
      // Mostrar toasts para notificaciones no mostradas
      for (const notification of notDisplayedNotifications) {
        try {
          console.log(`Procesando notificación no mostrada: ${notification.id} - ${notification.title}`);
          
          // Obtener datos relacionados para mostrar información más detallada
          const enhancedNotification = await getRelatedData(notification);
          
          let title = enhancedNotification.title;
          let message = enhancedNotification.message;
          
          // Si hay datos relacionados, personalizar el mensaje
          if ('relatedData' in enhancedNotification) {
            const relatedData = enhancedNotification.relatedData as any;
            
            if (notification.type === 'friend_request' && relatedData?.name) {
              message = `${relatedData.name} quiere ser tu amigo`;
            } else if (notification.type === 'routine_shared' && relatedData?.routineName && relatedData?.creatorName) {
              message = `${relatedData.creatorName} ha compartido contigo la rutina "${relatedData.routineName}"`;
            } else if (notification.type === 'friend_accepted' && relatedData?.name) {
              message = `${relatedData.name} ha aceptado tu solicitud de amistad`;
            }
          }
          
          // Mostrar notificación
          console.log(`Mostrando toast: ${title} - ${message}`);
          showToast('info', title, message);
          
          // Marcar como mostrada
          await updateDoc(doc(firestore, 'notifications', notification.id), {
            displayed: true
          });
        } catch (error) {
          console.error('Error al mostrar notificación:', error);
        }
      }
    }, (error) => {
      console.error("Error en el listener de notificaciones:", error);
      setLoading(false);
    });

    return () => {
      console.log("Desuscribiendo listener de notificaciones");
      unsubscribe();
    };
  }, [showToast]);

  return {
    notifications,
    unreadCount,
    friendRequestsCount,
    sharedRoutinesCount,
    loading,
    markAllAsRead,
    markAsRead,
    markRoutineNotificationsAsRead,
    markFriendRequestNotificationsAsRead
  };
}; 